import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { HistoryPeriod } from '@weenat/client/dist/core/history'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { useIntl } from '@weenat/wintl'
import Grid from '../kit/Grid'
import LoadingCircle from '../kit/loaders/LoadingCircle'
import Text from '../kit/Text'
import TextNumber from '../kit/TextNumber'
import PeriodBasedCumulativeSum from './PeriodBasedCumulativeSum'

interface AggregateBlockProps {
  label: string
  value: number | null
}

const AggregateBlock: FC<AggregateBlockProps> = ({ label, value }) => {
  return (
    <Flex
      $p={'md'}
      $gap={'md'}
      $justifyContent='space-between'
      $borderRadius={'md'}
      $backgroundColor={'grayscale.100'}
      $width={'100%'}
      $flex={1}
    >
      <Text>{label}</Text>
      <TextNumber $fontWeight='bold' $textAlign='right'>
        {value}
      </TextNumber>
    </Flex>
  )
}

interface PeriodBasedMetricDigestProps {
  metric: DeviceAvailableMeasure
  data: DataPoint[]
  isLoading: boolean
  period: HistoryPeriod
  timeStep: TimeSteps
  deviceId: number
}

const PeriodBasedMetricDigest: FC<PeriodBasedMetricDigestProps> = ({
  metric,
  data,
  isLoading,
  period,
  timeStep,
  deviceId
}) => {
  const { t } = useIntl()
  const { convertValue, convertDuration } = useConvertedValue()

  if (isLoading) {
    return <LoadingCircle />
  }

  const values = data
    .map((d) => d.value)
    .filter((v): v is number => v !== null && Number.isFinite(v))

  let avg: number | null = null
  let min: number | null = null
  let max: number | null = null
  let sum: number | null = null

  switch (metric) {
    case 'SSI':
      avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0
      min = values.length > 0 ? Math.min(...values) : 0
      max = values.length > 0 ? Math.max(...values) : 0

      return (
        <>
          <Flex $isDelimitedOnTop $p={'md'} $gap={'md'} $height={'auto'}>
            {/* Average */}
            <AggregateBlock
              label={t('charts.average', { capitalize: true })}
              value={convertValue({ metric, value: avg, displayUnit: true })}
            />
            {/* Maximum */}
            <AggregateBlock
              label={t('charts.max', { capitalize: true })}
              value={convertValue({ metric, value: max, displayUnit: true })}
            />
            {/* Minimum */}
            <AggregateBlock
              label={t('charts.min', { capitalize: true })}
              value={convertValue({ metric, value: min, displayUnit: true })}
            />
          </Flex>
          {/* Cumulative Sums */}
          <Grid $templateRows='1fr' $templateColumns='1fr 1fr' $columnGap={'md'} $p={'md'}>
            <PeriodBasedCumulativeSum
              metric={'SSI_HOURSAccumulation'}
              period={period}
              timeStep={timeStep}
              deviceId={deviceId}
            />
          </Grid>
        </>
      )
    case 'PPFD':
    case 'U':
    case 'T_SOIL':
    case 'T_WET':
    case 'T_DRY':
      avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0
      min = values.length > 0 ? Math.min(...values) : 0
      max = values.length > 0 ? Math.max(...values) : 0

      return (
        <Flex $p={'md'} $gap={'md'} $height={'auto'}>
          {/* Average */}
          <AggregateBlock
            label={t('charts.average', { capitalize: true })}
            value={convertValue({ metric, value: avg, displayUnit: true })}
          />

          {/* Maximum */}
          <AggregateBlock
            label={t('charts.max', { capitalize: true })}
            value={convertValue({ metric, value: max, displayUnit: true })}
          />

          {/* Minimum */}
          <AggregateBlock
            label={t('charts.min', { capitalize: true })}
            value={convertValue({ metric, value: min, displayUnit: true })}
          />
        </Flex>
      )

    case 'T':
      avg = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0
      min = values.length > 0 ? Math.min(...values) : 0
      max = values.length > 0 ? Math.max(...values) : 0
      const amp = Math.abs(max - min)

      return (
        <>
          <Flex $p={'md'} $gap={'md'} $height={'auto'}>
            {/* Average */}
            <AggregateBlock
              label={t('charts.average', { capitalize: true })}
              value={convertValue({ metric, value: avg, displayUnit: true })}
            />

            {/* Maximum */}
            <AggregateBlock
              label={t('charts.max', { capitalize: true })}
              value={convertValue({ metric, value: max, displayUnit: true })}
            />

            {/* Minimum */}
            <AggregateBlock
              label={t('charts.min', { capitalize: true })}
              value={convertValue({ metric, value: min, displayUnit: true })}
            />

            {/* Amplitude */}
            <AggregateBlock
              label={t('charts.spread', { capitalize: true })}
              value={convertValue({ metric, value: amp, displayUnit: true })}
            />
          </Flex>

          {/* Cumulative Sums */}
          <Grid $templateRows='1fr' $templateColumns='1fr 1fr' $columnGap={'md'} $p={'md'}>
            <PeriodBasedCumulativeSum
              metric={'heatAccumulation'}
              period={period}
              timeStep={timeStep}
              deviceId={deviceId}
            />
            <PeriodBasedCumulativeSum
              metric={'COLD_T_HOUR'}
              period={period}
              timeStep={timeStep}
              deviceId={deviceId}
            />
          </Grid>
        </>
      )

    case 'RR':
      {
        /* Total sum of rain */
      }
      sum = data.reduce((acc, d) => acc + (d.value ?? 0), 0)
      return (
        <Flex $p={'md'} $gap={'md'}>
          <AggregateBlock
            label={t('charts.cumulation', { capitalize: true })}
            value={convertValue({ metric, value: sum, displayUnit: true })}
          />
        </Flex>
      )

    // Not declaring LW_V on purpose to leave empty content
    case 'LW_D':
      sum = data.reduce((acc, d) => acc + (d.value ?? 0), 0)

      return (
        <Flex $p={'md'} $gap={'md'}>
          <AggregateBlock
            label={t('charts.cumulation', { capitalize: true })}
            value={convertDuration({ value: sum })}
          />
        </Flex>
      )

    default:
      return null
  }
}

export default PeriodBasedMetricDigest
